import { useState } from 'react';
import { useOffline, offlineStatus } from './';
import { classNames, numFormat } from '../../Services/helpers';
import useSyncProgress from '../../Data/Local/syncProgress';
import FailedWrites from './FailedWrites';

const Status = ({className}) => {
    const {status, sync, offlineSupported, queuedWrites} = useOffline();
    // Opened from this strip and nowhere else, so it stays off the shared context.
    const [errorsOpen, setErrorsOpen] = useState(false);

    const {pending, failed} = queuedWrites;
    const changes = (n) => `${numFormat(n)} change${n === 1 ? '' : 's'}`;

    const classes = {
        [offlineStatus.PENDING]: 'bg-brand text-white',
        [offlineStatus.ERROR]: 'bg-red-500 text-white',
        [offlineStatus.OFFLINE]: 'bg-gray-500 text-white',
        [offlineStatus.LOCAL_DATA]: 'relative overflow-hidden bg-brand text-white',
    }

    // "Changes may not be saved" is only true with no durable queue behind us.
    const message = {
        [offlineStatus.PENDING]: `${changes(pending)} waiting to send`,
        [offlineStatus.ERROR]: `${changes(failed)} couldn't be sent`,
        [offlineStatus.OFFLINE]: offlineSupported
            ? 'You\'re offline. Changes will be saved and sent when you\'re back.'
            : 'You are offline. Changes may not be saved.',
    }

    const onClicks = {
        [offlineStatus.PENDING]: sync,
        [offlineStatus.ERROR]: () => setErrorsOpen(true),
        [offlineStatus.OFFLINE]: sync,
    }

    // The modal sits outside the strip on both counts: discarding the last failed
    // write clears `status`, which would unmount it mid-task, and React propagates
    // portal clicks up the component tree, so every click inside it would fire the
    // strip's handler.
    return <>
        {status && <div className={classNames(className, classes[status] ?? 'bg-gray-500 text-white')}
            onClick={onClicks[status]}
        >
            {status === offlineStatus.LOCAL_DATA ? <LocalDataStatus /> : message[status]}
        </div>}

        <FailedWrites isOpen={errorsOpen} onClose={() => setErrorsOpen(false)} />
    </>
}

/**
 * The strip IS the progress bar — a lighter fill growing across the brand
 * background — because at 24px tall on mobile there is no room for the bar plus
 * caption of `Data/Local/SyncProgressBar`. Lightening the strip rather than
 * tinting a pale track keeps the white text legible at every percentage.
 */
const LocalDataStatus = () => {
    const {percent, downloaded, feed} = useSyncProgress();

    return <>
        <span
            aria-hidden
            className={classNames(
                'absolute inset-y-0 left-0 bg-white/25',
                percent === null ? 'w-1/3 animate-pulse' : 'transition-[width] duration-300 ease-out',
            )}
            style={percent === null ? undefined : {width: `${percent}%`}}
        />
        <span
            className="relative"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent ?? undefined}
        >
            Downloading...
            {' '}
            {percent === null ? `${numFormat(downloaded)} records` : `${percent}%`}
            {feed && ` · ${feed}`}
        </span>
    </>
}

export default Status;
